import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Circle, AlertTriangle, FileText, Download, ChevronDown, ChevronRight, Save, Loader2, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import jsPDF from 'jspdf';
import { loadTenantBrand } from '@/components/tools/shared/tenantBrand';

const API = process.env.REACT_APP_BACKEND_URL;

const CHECKLIST_TEMPLATES = {
  'new-construction': {
    label: 'New Construction',
    trades: {
      'Structural': [
        { item: 'Foundation type determined and documented', code: 'IRC R401-R404' },
        { item: 'Footing dimensions per soil bearing capacity', code: 'IRC R403.1' },
        { item: 'Anchor bolt spacing and embedment', code: 'IRC R403.1.6' },
        { item: 'Floor joist species, grade, and spacing', code: 'IRC R502.3' },
        { item: 'Bearing wall identification and headers sized', code: 'IRC R602.7' },
        { item: 'Shear wall bracing method documented', code: 'IRC R602.10' },
        { item: 'Roof framing: rafter/truss sizing and spacing', code: 'IRC R802' },
        { item: 'Point loads identified and bearing path traced', code: 'IRC R301' },
      ],
      'Mechanical': [
        { item: 'HVAC load calculation (Manual J or equivalent)', code: 'IRC M1401.3' },
        { item: 'Duct sizing and layout', code: 'IRC M1601' },
        { item: 'Combustion air for gas appliances', code: 'IRC G2407' },
        { item: 'Exhaust fan CFM: bathroom 50, kitchen 100', code: 'IRC M1507' },
        { item: 'Dryer exhaust vent length and material', code: 'IRC M1502' },
        { item: 'Gas piping sizing per BTU demand', code: 'IRC G2413' },
      ],
      'Electrical': [
        { item: 'Panel size adequate (200A typical residential)', code: 'NEC 230.79' },
        { item: 'Outlet spacing: 6ft from door, 12ft intervals', code: 'NEC 210.52' },
        { item: 'GFCI: kitchen, bath, garage, outdoor, laundry', code: 'NEC 210.8' },
        { item: 'AFCI: bedrooms, living areas (all habitable)', code: 'NEC 210.12' },
        { item: 'Dedicated circuits: kitchen (2), laundry, bath', code: 'NEC 210.11' },
        { item: 'Smoke/CO detector locations and wiring', code: 'IRC R314/R315' },
        { item: 'EV charger rough-in (if applicable)', code: 'Local codes' },
      ],
      'Plumbing': [
        { item: 'Water supply pipe sizing', code: 'IRC P2903' },
        { item: 'DWV pipe sizing and slope (1/4"/ft min)', code: 'IRC P3005' },
        { item: 'Fixture unit count and main drain sizing', code: 'IRC P3004' },
        { item: 'Water heater sizing and location', code: 'IRC P2801' },
        { item: 'Cleanout access locations', code: 'IRC P3005.2' },
        { item: 'Backflow prevention', code: 'IRC P2902' },
      ],
      'Energy': [
        { item: 'Wall insulation R-value meets climate zone', code: 'IECC Table R402.1.2' },
        { item: 'Ceiling insulation R-value meets climate zone', code: 'IECC Table R402.1.2' },
        { item: 'Window U-factor and SHGC per climate zone', code: 'IECC Table R402.1.2' },
        { item: 'Air barrier and sealing plan', code: 'IECC R402.4' },
        { item: 'Duct sealing and insulation', code: 'IECC R403.3' },
        { item: 'Lighting: 75% high efficacy (LED)', code: 'IECC R404.1' },
      ],
    }
  },
  'addition': {
    label: 'Addition',
    trades: {
      'Structural': [
        { item: 'Existing foundation capacity for new loads', code: 'IRC R401' },
        { item: 'Tie-in method to existing structure', code: 'IRC R301' },
        { item: 'New headers for openings in existing walls', code: 'IRC R602.7' },
        { item: 'Roof tie-in: valley, ridge, drainage', code: 'IRC R802' },
      ],
      'Mechanical': [
        { item: 'Existing HVAC capacity for added space', code: 'IRC M1401' },
        { item: 'New ductwork routing and sizing', code: 'IRC M1601' },
      ],
      'Electrical': [
        { item: 'Panel capacity for additional circuits', code: 'NEC 220' },
        { item: 'AFCI/GFCI for new circuits', code: 'NEC 210.8/210.12' },
      ],
      'Plumbing': [
        { item: 'Existing main capacity for new fixtures', code: 'IRC P2903' },
        { item: 'Tie-in points to existing DWV', code: 'IRC P3005' },
      ],
      'Energy': [
        { item: 'Addition meets current energy code', code: 'IECC R502' },
        { item: 'Existing envelope not downgraded', code: 'IECC R502.1' },
      ],
    }
  },
  'remodel': {
    label: 'Remodel',
    trades: {
      'Structural': [
        { item: 'Wall removal analysis (bearing vs partition)', code: 'IRC R301' },
        { item: 'New beam/header sizing for openings', code: 'IRC R602.7' },
        { item: 'Floor load changes (new use = new loads)', code: 'IRC R301.5' },
      ],
      'Electrical': [
        { item: 'Upgrade outlets to GFCI/AFCI per current code', code: 'NEC 210' },
        { item: 'Panel evaluation for increased load', code: 'NEC 220' },
      ],
      'Plumbing': [
        { item: 'Fixture relocation: vent and drain compliance', code: 'IRC P3100' },
      ],
      'Energy': [
        { item: 'Insulation upgrade where exposed', code: 'IECC R503' },
      ],
    }
  },
};

export default function CodeCompliance({ darkMode = false, projectId = null, siteContext = null }) {
  const [projectType, setProjectType] = useState('');
  const [checklist, setChecklist] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [notes, setNotes] = useState({});
  const [expandedTrade, setExpandedTrade] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [loading, setLoading] = useState(false);
  const saveTimerRef = useRef(null);

  const bg = darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F8F7F5]';
  const cardBg = darkMode ? 'bg-[#1A1A1E]' : 'bg-white';
  const border = darkMode ? 'border-[#2A2A2A]' : 'border-gray-200';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';

  // Load saved checklist when project changes
  useEffect(() => {
    if (!projectId) return;
    const loadSaved = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/design-companion/compliance/${projectId}`);
        const saved = res.data?.checklist;
        if (saved && saved.project_type && CHECKLIST_TEMPLATES[saved.project_type]) {
          setProjectType(saved.project_type);
          setChecklist(CHECKLIST_TEMPLATES[saved.project_type]);
          setStatuses(saved.statuses || {});
          setNotes(saved.notes || {});
          setLastSaved(saved.updated_at);
          setExpandedTrade(Object.keys(CHECKLIST_TEMPLATES[saved.project_type].trades)[0]);
        }
      } catch {}
      setLoading(false);
    };
    loadSaved();
  }, [projectId]);

  // Auto-save debounced when statuses or notes change (only if project selected)
  useEffect(() => {
    if (!projectId || !checklist || !projectType) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.put(`${API}/api/design-companion/compliance/${projectId}`, {
          project_type: projectType,
          statuses,
          notes,
        });
        if (res.data?.updated_at) setLastSaved(res.data.updated_at);
      } catch {}
    }, 1500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [statuses, notes, projectId, checklist, projectType]);

  const generateChecklist = () => {
    if (!projectType) { toast.error('Select a project type'); return; }
    const template = CHECKLIST_TEMPLATES[projectType];
    setChecklist(template);
    setStatuses({});
    setNotes({});
    setExpandedTrade(Object.keys(template.trades)[0]);
  };

  const toggleStatus = (trade, idx) => {
    const key = `${trade}_${idx}`;
    setStatuses(prev => {
      const current = prev[key] || 'pending';
      const next = current === 'pending' ? 'pass' : current === 'pass' ? 'fail' : current === 'fail' ? 'na' : 'pending';
      return { ...prev, [key]: next };
    });
  };

  const getTradeScore = (trade) => {
    const items = checklist?.trades[trade] || [];
    let pass = 0, total = 0;
    items.forEach((_, i) => {
      const s = statuses[`${trade}_${i}`] || 'pending';
      if (s !== 'na') total++;
      if (s === 'pass') pass++;
    });
    return total > 0 ? Math.round((pass / total) * 100) : 0;
  };

  const getOverallScore = () => {
    if (!checklist) return 0;
    let pass = 0, total = 0;
    Object.keys(checklist.trades).forEach(trade => {
      (checklist.trades[trade] || []).forEach((_, i) => {
        const s = statuses[`${trade}_${i}`] || 'pending';
        if (s !== 'na') total++;
        if (s === 'pass') pass++;
      });
    });
    return total > 0 ? Math.round((pass / total) * 100) : 0;
  };

  const exportChecklist = () => {
    if (!checklist) return;
    let csv = 'Trade,Item,Code Reference,Status,Notes\n';
    Object.keys(checklist.trades).forEach(trade => {
      checklist.trades[trade].forEach((item, i) => {
        const status = statuses[`${trade}_${i}`] || 'pending';
        const note = notes[`${trade}_${i}`] || '';
        csv += `"${trade}","${item.item}","${item.code}","${status}","${note}"\n`;
      });
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `compliance_checklist_${projectType}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    if (!checklist) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 18;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const addPage = () => { doc.addPage(); y = margin; };
    const checkSpace = (needed) => { if (y + needed > 260) addPage(); };

    // Header bar
    doc.setFillColor(112, 23, 15); // #70170F
    doc.rect(0, 0, pageWidth, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Permit Readiness Report', margin, 14);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, 22);
    // DP67 Part 4b — a permit report is TENANT output. Our name does not go on
    // it. The tenant's own name, or nothing.
    const pdfBrand = await loadTenantBrand();
    if (pdfBrand.companyName) {
      doc.text(pdfBrand.companyName, pageWidth - margin, 22, { align: 'right' });
    }
    y = 38;

    // Overall Score
    doc.setTextColor(40, 40, 40);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Project Type: ${checklist.label}`, margin, y);
    y += 6;
    const scoreColor = overallScore >= 80 ? [34, 197, 94] : overallScore >= 50 ? [245, 158, 11] : [156, 163, 175];
    doc.setFontSize(32);
    doc.setTextColor(...scoreColor);
    doc.text(`${overallScore}%`, margin, y + 10);
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text('Overall Readiness', margin + 28, y + 10);
    y += 20;

    // Trade summary row
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
    const trades = Object.keys(checklist.trades);
    const tradeColWidth = contentWidth / trades.length;
    trades.forEach((trade, idx) => {
      const score = getTradeScore(trade);
      const x = margin + idx * tradeColWidth;
      const sc = score >= 80 ? [34, 197, 94] : score >= 50 ? [245, 158, 11] : score > 0 ? [239, 68, 68] : [160, 160, 160];
      doc.setFontSize(14);
      doc.setTextColor(...sc);
      doc.setFont('helvetica', 'bold');
      doc.text(score > 0 ? `${score}%` : '--', x + tradeColWidth / 2, y, { align: 'center' });
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.setFont('helvetica', 'normal');
      doc.text(trade, x + tradeColWidth / 2, y + 5, { align: 'center' });
    });
    y += 14;
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    // Trade details
    trades.forEach(trade => {
      checkSpace(20);
      const items = checklist.trades[trade];
      const score = getTradeScore(trade);
      // Trade header
      doc.setFillColor(245, 245, 242);
      doc.roundedRect(margin, y - 3, contentWidth, 9, 1.5, 1.5, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(trade, margin + 3, y + 3);
      const sc = score >= 80 ? [34, 197, 94] : score >= 50 ? [245, 158, 11] : score > 0 ? [239, 68, 68] : [160, 160, 160];
      doc.setTextColor(...sc);
      doc.text(`${score}%`, pageWidth - margin - 3, y + 3, { align: 'right' });
      y += 10;

      items.forEach((item, i) => {
        checkSpace(10);
        const key = `${trade}_${i}`;
        const status = statuses[key] || 'pending';
        const note = notes[key] || '';
        // Status icon
        if (status === 'pass') { doc.setFillColor(34, 197, 94); doc.circle(margin + 3, y, 1.8, 'F'); }
        else if (status === 'fail') { doc.setFillColor(239, 68, 68); doc.circle(margin + 3, y, 1.8, 'F'); }
        else if (status === 'na') { doc.setFillColor(180, 180, 180); doc.circle(margin + 3, y, 1.2, 'F'); }
        else { doc.setDrawColor(180, 180, 180); doc.circle(margin + 3, y, 1.8); }
        // Item text
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(60, 60, 60);
        const itemText = doc.splitTextToSize(item.item, contentWidth - 40);
        doc.text(itemText, margin + 8, y + 1);
        // Code ref
        doc.setFontSize(7);
        doc.setTextColor(140, 140, 140);
        doc.text(item.code, pageWidth - margin - 2, y + 1, { align: 'right' });
        y += itemText.length * 4 + 1;
        // Note if any
        if (note) {
          doc.setFontSize(7);
          doc.setTextColor(100, 100, 100);
          doc.setFont('helvetica', 'italic');
          const noteLines = doc.splitTextToSize(`Note: ${note}`, contentWidth - 15);
          checkSpace(noteLines.length * 3.5);
          doc.text(noteLines, margin + 8, y);
          y += noteLines.length * 3.5 + 1;
        }
      });
      y += 4;
    });

    // Footer
    checkSpace(15);
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.setFont('helvetica', 'normal');
    doc.text('This report is generated as a design aid and does not replace professional engineering or code official review.', margin, y);

    doc.save(`permit_readiness_${projectType}_${new Date().toISOString().slice(0,10)}.pdf`);
    toast.success('PDF exported');
  };

  const StatusIcon = ({ status }) => {
    if (status === 'pass') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'fail') return <AlertTriangle className="w-4 h-4 text-red-500" />;
    if (status === 'na') return <span className="text-[10px] font-bold text-gray-400 w-4 text-center">N/A</span>;
    return <Circle className="w-4 h-4 text-gray-300" />;
  };

  const overallScore = getOverallScore();

  return (
    <div className={`p-6 space-y-6 ${bg}`} data-testid="code-compliance">
      <div>
        <div className="flex items-center justify-between">
          <h2 className={`text-lg font-semibold mb-1 ${text}`}>Code & Compliance</h2>
          {projectId && lastSaved && (
            <div className={`flex items-center gap-1.5 text-[10px] ${textMuted}`} data-testid="compliance-save-status">
              <Save className="w-3 h-3" />
              Saved {new Date(lastSaved).toLocaleTimeString()}
            </div>
          )}
        </div>
        <p className={`text-sm ${textMuted}`}>Generate trade-by-trade compliance checklists mapped to IRC 2018 and NEC. Track readiness for permit submission.</p>
        {siteContext?.climate_zone && (
          <div className={`flex items-center gap-2 mt-2 px-3 py-1.5 rounded-lg text-[11px] ${darkMode ? 'bg-blue-900/15 text-blue-300' : 'bg-blue-50 text-blue-700'}`} data-testid="site-context-indicator">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            Climate Zone {siteContext.climate_zone}
            {siteContext.irc_version && <span>| IRC {siteContext.irc_version}</span>}
            {siteContext.address?.city && <span>| {siteContext.address.city}, {siteContext.address.state}</span>}
          </div>
        )}
        {!projectId && (
          <div className={`mt-2 text-[11px] ${textMuted} italic`}>Select a project to auto-save checklist progress</div>
        )}
      </div>

      {/* Project type selector */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-[#BC5D41]" />
          <span className={`ml-2 text-sm ${textMuted}`}>Loading saved checklist...</span>
        </div>
      ) : (
      <>
      <div className={`${cardBg} border ${border} rounded-xl p-5 flex items-end gap-4`}>
        <div className="flex-1">
          <label className={`text-[11px] font-medium ${textMuted}`}>Project Type</label>
          <Select value={projectType} onValueChange={setProjectType}>
            <SelectTrigger className={`mt-1 ${darkMode ? 'bg-[#2A2A2E] border-[#444] text-gray-100' : ''}`} data-testid="project-type-select">
              <SelectValue placeholder="Select project type..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new-construction">New Construction</SelectItem>
              <SelectItem value="addition">Addition</SelectItem>
              <SelectItem value="remodel">Remodel</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={generateChecklist} className="bg-[#BC5D41] hover:bg-[#70170F] text-white" data-testid="generate-checklist-btn">
          Generate Checklist
        </Button>
      </div>

      {/* Permit Readiness Score */}
      {checklist && (
        <div className={`${cardBg} border ${border} rounded-xl p-5`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-sm font-semibold ${text}`}>Permit Readiness — {checklist.label}</h3>
            <div className="flex items-center gap-3">
              <div className={`text-2xl font-bold ${overallScore >= 80 ? 'text-green-500' : overallScore >= 50 ? 'text-amber-500' : textMuted}`}
                data-testid="overall-score">{overallScore}%</div>
              <Button variant="outline" size="sm" onClick={exportChecklist} className="text-xs gap-1" data-testid="export-checklist-btn">
                <Download className="w-3 h-3" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={exportPDF} className="text-xs gap-1" data-testid="export-pdf-btn">
                <FileText className="w-3 h-3" /> PDF
              </Button>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            {Object.keys(checklist.trades).map(trade => {
              const score = getTradeScore(trade);
              return (
                <button key={trade} onClick={() => setExpandedTrade(expandedTrade === trade ? null : trade)}
                  className={`px-4 py-3 rounded-lg border ${expandedTrade === trade ? 'border-[#BC5D41]' : border} text-center transition-colors hover:border-[#BC5D41]/50`}
                  data-testid={`trade-btn-${trade}`}>
                  <div className={`text-xl font-bold ${score >= 80 ? 'text-green-500' : score >= 50 ? 'text-amber-500' : score > 0 ? 'text-red-400' : textMuted}`}>
                    {score > 0 ? `${score}%` : '—'}
                  </div>
                  <div className={`text-[10px] font-medium ${textMuted} mt-0.5`}>{trade}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Checklist items by trade */}
      {checklist && expandedTrade && checklist.trades[expandedTrade] && (
        <div className={`${cardBg} border ${border} rounded-xl overflow-hidden`} data-testid="checklist-items">
          <div className={`px-4 py-3 border-b ${border} flex items-center justify-between`}>
            <h3 className={`text-sm font-semibold ${text}`}>{expandedTrade}</h3>
            <div className="flex gap-3 text-[10px]">
              <span className="flex items-center gap-1"><Circle className="w-3 h-3 text-gray-300" /> Pending</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Pass</span>
              <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-500" /> Fail</span>
              <span className="text-gray-400">N/A</span>
            </div>
          </div>
          <div>
            {checklist.trades[expandedTrade].map((item, idx) => {
              const key = `${expandedTrade}_${idx}`;
              const status = statuses[key] || 'pending';
              return (
                <div key={idx} className={`px-4 py-3 border-b last:border-0 ${border} flex items-start gap-3 ${status === 'fail' ? (darkMode ? 'bg-red-900/10' : 'bg-red-50/50') : ''}`}>
                  <button onClick={() => toggleStatus(expandedTrade, idx)} className="mt-0.5 flex-shrink-0" data-testid={`checklist-toggle-${idx}`}>
                    <StatusIcon status={status} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs ${text} ${status === 'pass' ? 'line-through opacity-60' : ''}`}>{item.item}</div>
                    <div className={`text-[10px] ${textMuted} mt-0.5`}>{item.code}</div>
                    {status === 'fail' && (
                      <Input placeholder="Note why this fails..."
                        className={`mt-2 h-7 text-xs ${darkMode ? 'bg-[#2A2A2E] border-[#444] text-gray-100' : ''}`}
                        value={notes[key] || ''}
                        onChange={e => setNotes(prev => ({ ...prev, [key]: e.target.value }))}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
